import { useCallback, useMemo } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";

import type { AppRole } from "../../lib/auth";
import {
  ALL_ADMIN_PERMISSIONS,
  defaultPermissionsForRole,
  isSuperAdminRole,
  PERMISSION_LABELS,
} from "../../lib/adminPermissions";
import { STROKES } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";
import { AdminButton, AdminCheckbox } from "./ui";

const THEME = PANE_THEMES.Admin;

type Props = {
  role: AppRole | null;
  permissions: string[];
  onChange: (next: string[]) => void;
};

export function PermissionsPicker({ role, permissions, onChange }: Props) {
  const set = useMemo(() => new Set(permissions), [permissions]);
  const superAdmin = isSuperAdminRole(role);

  const toggle = useCallback(
    (key: string) => {
      if (superAdmin) return;
      const next = new Set(set);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onChange([...next]);
    },
    [set, onChange, superAdmin]
  );

  const selectAll = useCallback(() => {
    if (superAdmin) return;
    onChange([...ALL_ADMIN_PERMISSIONS]);
  }, [onChange, superAdmin]);

  const clearAll = useCallback(() => {
    if (superAdmin) return;
    onChange([]);
  }, [onChange, superAdmin]);

  const resetDefaults = useCallback(() => {
    if (superAdmin || !role) return;
    onChange(defaultPermissionsForRole(role));
  }, [onChange, role, superAdmin]);

  return (
    <View style={{ gap: 12 }}>
      {superAdmin ? (
        <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
          super_admin has all permissions by default — individual toggles are not used.
        </Text>
      ) : (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <AdminButton theme={THEME} label="Select all" variant="outline" size="sm" onPress={selectAll} />
          <AdminButton theme={THEME} label="Clear all" variant="outline" size="sm" onPress={clearAll} />
          <AdminButton theme={THEME} label="Reset to role defaults" variant="outline" size="sm" onPress={resetDefaults} />
        </View>
      )}
      {ALL_ADMIN_PERMISSIONS.map((key) => {
        const on = superAdmin || set.has(key);
        return (
          <Pressable
            key={key}
            disabled={superAdmin}
            onPress={() => toggle(key)}
            style={[
              {
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                borderBottomWidth: STROKES.hairline,
                borderBottomColor: THEME.divider,
                paddingVertical: 8,
              },
              Platform.OS === "web" && !superAdmin ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
            ]}
          >
            <AdminCheckbox theme={THEME} checked={on} onPress={() => toggle(key)} size={20} />
            <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 13, color: superAdmin ? THEME.textOnSurfaceFaint : THEME.textOnSurface }}>
              {PERMISSION_LABELS[key]}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
